import React, { useRef, useEffect, useState, useMemo } from "react";
import Icon from "../../../components/AppIcon";

const Waveform = ({ id, audioBuffer, currentTime, duration, loopActive, loopStart, loopEnd, cues, onSeek, color = "primary" }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1); // Zoom level 1x, 2x, 4x, 8x
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [localTime, setLocalTime] = useState(currentTime);
  const [scrollOffsetPoints, setScrollOffsetPoints] = useState(0);

  // Drag tracking refs
  const startX = useRef(0);
  const dragStartOffset = useRef(0);
  const hasMoved = useRef(false);

  // Sync prop changes to local time state (e.g., when seek or load occurs)
  useEffect(() => {
    setLocalTime(currentTime);
  }, [currentTime]);

  // High-performance requestAnimationFrame loop to poll time when playing
  useEffect(() => {
    let animId = null;

    const pollTime = () => {
      if (window.djEngine) {
        const deck = window.djEngine.decks.get(id);
        if (deck) {
          const t = window.djEngine.getDeckCurrentTime(id);
          setLocalTime(t);
        }
      }
      animId = requestAnimationFrame(pollTime);
    };

    animId = requestAnimationFrame(pollTime);
    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [id]);

  const colors = useMemo(() => {
    const themeColors = {
      primary: { // Hacker Green / Matrix style
        waveBass: "#14532d",   // Dark forest green
        waveMid: "#00ff41",    // Matrix neon green
        waveHigh: "#86efac",   // Light emerald green
        playhead: "#00ff41",
        loopBg: "rgba(0, 255, 65, 0.12)",
        loopBorder: "#00ff41"
      },
      cyan: { // Cyber Cyan
        waveBass: "#0f3a44",   // Dark slate teal
        waveMid: "#00ffcc",    // Neon cyan
        waveHigh: "#a5f3fc",   // Ice blue
        playhead: "#00ffcc",
        loopBg: "rgba(0, 255, 204, 0.12)",
        loopBorder: "#00ffcc"
      },
      blue: { // Digital Blue
        waveBass: "#1e1b4b",   // Midnight blue
        waveMid: "#3b82f6",    // Electric blue
        waveHigh: "#93c5fd",   // Sky blue
        playhead: "#3b82f6",
        loopBg: "rgba(59, 130, 246, 0.12)",
        loopBorder: "#3b82f6"
      },
      warning: { // Amber Warning
        waveBass: "#451a03",   // Deep rust
        waveMid: "#f59e0b",    // Neon amber
        waveHigh: "#fde047",   // Neon yellow
        playhead: "#f59e0b",
        loopBg: "rgba(245, 158, 11, 0.12)",
        loopBorder: "#f59e0b"
      }
    };
    return themeColors[color] || themeColors.primary;
  }, [color]);

  // Downsample audio buffer to displayable resolution
  const waveformData = useMemo(() => {
    if (!audioBuffer) return null;

    const rawData = audioBuffer.getChannelData(0); // Left channel
    const numPoints = 1200; // Resolution of full track waveform
    const step = Math.floor(rawData.length / numPoints);
    const peaks = [];

    // Analyze three frequency zones per step to draw multi-band colored waveforms
    for (let i = 0; i < numPoints; i++) {
      const start = i * step;
      const end = start + step;
      let maxBass = 0;
      let maxMid = 0;
      let maxHigh = 0;

      // Scan step slice
      for (let j = start; j < end; j += Math.max(1, Math.floor(step / 80))) {
        const val = Math.abs(rawData[j] || 0);
        
        // Simulating band splits based on sample indexes
        if (j % 3 === 0) {
          if (val > maxBass) maxBass = val;
        } else if (j % 3 === 1) {
          if (val > maxMid) maxMid = val;
        } else {
          if (val > maxHigh) maxHigh = val;
        }
      }

      peaks.push({
        bass: Math.min(1.0, maxBass * 1.5),
        mid: Math.min(1.0, maxMid * 1.3),
        high: Math.min(1.0, maxHigh * 1.0)
      });
    }

    return peaks;
  }, [audioBuffer]);

  // Handle Canvas Drawing Loop based on localTime and scrollOffsetPoints updates
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, width, height);

    // If no track is loaded, draw empty grid scan lines
    if (!waveformData || duration === 0) {
      ctx.strokeStyle = "rgba(125, 133, 144, 0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();

      // Draw grid lines
      ctx.strokeStyle = "rgba(125, 133, 144, 0.08)";
      for (let x = 20; x < width; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      ctx.fillStyle = "rgba(125, 133, 144, 0.35)";
      ctx.font = "9px monospace";
      ctx.fillText("NO TRACK LOADED - CUE AUDIO SOURCE", 15, height / 2 + 3);
      return;
    }

    // Scrolling Window Calculation (centered around playhead + scrollOffset)
    const progress = localTime / duration;
    const totalPoints = waveformData.length;
    
    // Zoom controls visible window: zoom = 1 -> view full song. zoom = 8 -> view 1/8th of song
    const visiblePointsCount = Math.round(totalPoints / zoom);
    const centerPointIdx = Math.round(progress * totalPoints);
    
    const naturalStartIdx = Math.round(centerPointIdx - visiblePointsCount / 2);
    let startPointIdx = naturalStartIdx + Math.round(scrollOffsetPoints);
    
    // Clamp startPointIdx to valid boundaries
    startPointIdx = Math.max(0, Math.min(totalPoints - visiblePointsCount, startPointIdx));
    const endPointIdx = Math.min(totalPoints, startPointIdx + visiblePointsCount);

    const barWidth = width / visiblePointsCount;

    // Draw Grid Lines (every 2 seconds)
    const beatsSec = 2; 
    ctx.strokeStyle = "rgba(255, 255, 255, 0.06)";
    ctx.lineWidth = 0.5;
    for (let sec = 0; sec < duration; sec += beatsSec) {
      const secProgress = sec / duration;
      const secPointIdx = secProgress * totalPoints;
      if (secPointIdx >= startPointIdx && secPointIdx <= endPointIdx) {
        const x = (secPointIdx - startPointIdx) * barWidth;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    }

    // Draw Loop Range Overlay
    if (loopActive && loopStart !== null && loopEnd !== null) {
      const loopStartPct = loopStart / duration;
      const loopEndPct = loopEnd / duration;
      
      const startIdx = loopStartPct * totalPoints;
      const endIdx = loopEndPct * totalPoints;

      const loopX1 = Math.max(0, (startIdx - startPointIdx) * barWidth);
      const loopX2 = Math.min(width, (endIdx - startPointIdx) * barWidth);

      if (loopX2 > loopX1) {
        ctx.fillStyle = colors.loopBg;
        ctx.fillRect(loopX1, 0, loopX2 - loopX1, height);
        
        ctx.strokeStyle = colors.loopBorder;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(loopX1, 0);
        ctx.lineTo(loopX1, height);
        ctx.moveTo(loopX2, 0);
        ctx.lineTo(loopX2, height);
        ctx.stroke();
      }
    }

    // Draw Cue Markers
    if (cues) {
      cues.forEach((cueTime, index) => {
        const cuePct = cueTime / duration;
        const cueIdx = cuePct * totalPoints;
        if (cueIdx >= startPointIdx && cueIdx <= endPointIdx) {
          const x = (cueIdx - startPointIdx) * barWidth;
          
          // Draw marker flag
          ctx.fillStyle = "#fbbf24";
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x + 5, 5);
          ctx.lineTo(x + 5, 14);
          ctx.lineTo(x - 5, 14);
          ctx.lineTo(x - 5, 5);
          ctx.closePath();
          ctx.fill();

          ctx.strokeStyle = "#fbbf24";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, 14);
          ctx.lineTo(x, height);
          ctx.stroke();

          ctx.fillStyle = "#000000";
          ctx.font = "bold 8px monospace";
          ctx.fillText(`C${index + 1}`, x - 5, 11);
        }
      });
    }

    // Draw Multi-band Waveform bars
    for (let i = startPointIdx; i < endPointIdx; i++) {
      const point = waveformData[i];
      const x = (i - startPointIdx) * barWidth;
      
      const hBass = point.bass * (height * 0.42);
      const hMid = point.mid * (height * 0.36);
      const hHigh = point.high * (height * 0.22);

      const centerY = height / 2;

      // Draw Bass (outermost)
      ctx.fillStyle = colors.waveBass;
      ctx.fillRect(x, centerY - hBass, Math.max(1, barWidth - 0.5), hBass * 2);

      // Draw Mids (layered inside)
      ctx.fillStyle = colors.waveMid;
      ctx.fillRect(x, centerY - hMid, Math.max(1, barWidth - 0.5), hMid * 2);

      // Draw Highs (innermost)
      ctx.fillStyle = colors.waveHigh;
      ctx.fillRect(x, centerY - hHigh, Math.max(1, barWidth - 0.5), hHigh * 2);
    }

    // Draw Current Playhead Line (Styled like a neon terminal needle)
    const playheadX = (centerPointIdx - startPointIdx) * barWidth;
    
    // Draw playhead only if it's within the visible scrolled window bounds
    if (playheadX >= 0 && playheadX <= width) {
      ctx.strokeStyle = colors.playhead;
      ctx.lineWidth = 2.5;
      ctx.shadowColor = colors.playhead;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(playheadX, 0);
      ctx.lineTo(playheadX, height);
      ctx.stroke();
      ctx.shadowBlur = 0; // reset
    }

  }, [waveformData, localTime, duration, zoom, loopActive, loopStart, loopEnd, cues, colors, scrollOffsetPoints]);

  const handlePointerDown = (e) => {
    if (!canvasRef.current || duration === 0) return;
    setIsMouseDown(true);
    hasMoved.current = false;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    startX.current = clientX;
    dragStartOffset.current = scrollOffsetPoints;
  };

  const handlePointerMove = (e) => {
    if (!isMouseDown || !canvasRef.current || duration === 0) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - startX.current;
    
    // If movement is larger than threshold, transition to dragging mode
    if (Math.abs(deltaX) > 4) {
      hasMoved.current = true;
    }

    if (hasMoved.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const totalPoints = waveformData ? waveformData.length : 1200;
      const visiblePointsCount = totalPoints / zoom;
      
      // dragging left shifts view right (increases scrollOffsetPoints)
      const pointsPerPixel = visiblePointsCount / rect.width;
      const deltaPoints = -deltaX * pointsPerPixel;
      
      const progress = localTime / duration;
      const centerPointIdx = progress * totalPoints;
      const naturalStartIdx = centerPointIdx - visiblePointsCount / 2;

      // Clamp scrollOffsetPoints so view doesn't scroll off track boundaries
      const maxScroll = totalPoints - visiblePointsCount - naturalStartIdx;
      const minScroll = -naturalStartIdx;
      const clampedOffset = Math.max(minScroll, Math.min(maxScroll, dragStartOffset.current + deltaPoints));
      
      setScrollOffsetPoints(clampedOffset);
    }
  };

  const handlePointerUp = (e) => {
    if (!isMouseDown) return;
    setIsMouseDown(false);

    // If they did not drag, treat it as a click and seek the song!
    if (!hasMoved.current && canvasRef.current && duration > 0) {
      const rect = canvasRef.current.getBoundingClientRect();
      const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
      const x = clientX - rect.left;
      const pct = Math.max(0, Math.min(1.0, x / rect.width));

      const totalPoints = waveformData ? waveformData.length : 1200;
      const progress = localTime / duration;
      const centerPointIdx = progress * totalPoints;
      const visiblePointsCount = totalPoints / zoom;
      const naturalStartIdx = centerPointIdx - visiblePointsCount / 2;
      const startPointIdx = Math.max(0, Math.min(totalPoints - visiblePointsCount, naturalStartIdx + scrollOffsetPoints));

      const clickedPointIdx = startPointIdx + pct * visiblePointsCount;
      const targetPct = clickedPointIdx / totalPoints;
      const seekTime = Math.max(0, Math.min(duration, targetPct * duration));

      if (onSeek) {
        onSeek(seekTime);
      }
      
      // Auto snap back to playhead on click seek
      setScrollOffsetPoints(0);
    }
  };

  useEffect(() => {
    const handleGlobalUp = () => setIsMouseDown(false);
    window.addEventListener("mouseup", handleGlobalUp);
    return () => window.removeEventListener("mouseup", handleGlobalUp);
  }, []);

  return (
    <div ref={containerRef} className="w-full flex flex-col space-y-1.5 select-none relative">
      {/* Waveform Header: Zoom controls and Time details */}
      <div className="flex justify-between items-center text-xs font-fira px-1">
        <div className="flex space-x-1.5 items-center">
          <span className="text-[9px] text-muted-foreground uppercase tracking-widest">Deck {id} Waveform</span>
          {audioBuffer && (
            <span className="bg-black/40 px-1 rounded text-[8px] text-[#00ff41] animate-pulse border border-[#00ff41]/20">
              HD PCM
            </span>
          )}
        </div>

        {/* Zoom Controls */}
        <div className="flex space-x-1 items-center bg-zinc-900 border border-zinc-800 rounded p-0.5 z-10">
          <span className="text-[8px] text-muted-foreground mr-1 uppercase">Zoom</span>
          {[1, 2, 4, 8].map((z) => (
            <button
              key={z}
              onClick={() => setZoom(z)}
              className={`px-1.5 py-0.5 rounded text-[9px] font-bold transition ${
                zoom === z
                  ? "bg-zinc-800 text-[#00ff41] border border-[#00ff41]/30 font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-zinc-800/40"
              }`}
            >
              {z}x
            </button>
          ))}
        </div>
      </div>

      {/* Main Canvas Waveform */}
      <div className="relative h-14 bg-zinc-950 border border-zinc-900 rounded-lg overflow-hidden group">
        
        {/* Floating Snap to Playhead Overlay button */}
        {scrollOffsetPoints !== 0 && (
          <button 
            onClick={() => setScrollOffsetPoints(0)}
            className="absolute top-1.5 left-1/2 -translate-x-1/2 z-20 px-2 py-0.5 bg-black/85 hover:bg-black border border-[#00ff41]/30 hover:border-[#00ff41]/60 text-[#00ff41] rounded text-[8px] font-bold font-fira tracking-widest flex items-center space-x-1 transition duration-150 animate-pulse shadow-md"
          >
            <Icon name="Anchor" size={8} />
            <span>SNAP TO PLAYHEAD</span>
          </button>
        )}

        <canvas
          ref={canvasRef}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          className="w-full h-full cursor-pointer relative z-10"
          style={{ touchAction: "none" }}
        />
        
        {/* Glow behind playing head */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00ff41]/5 to-transparent pointer-events-none z-0"></div>
      </div>
    </div>
  );
};

export default Waveform;
